/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/indent */
import React, { type RefObject, useEffect, useRef, useState, useMemo } from 'react';
import Image from 'next/legacy/image';
import ReactMarkdown from 'react-markdown';
import GoogleMap from '@components/common/Map/Map';
import isShowReadMoreText from '@helpers/readMore';
import warningIcon from '@assets/images/warning-icon.svg';
import infoIcon from '@assets/images/info-icon.svg';
import { useCookies } from 'react-cookie';
import useSessionStorage from '@hooks/useSessionStorage';
import FavoritesToastMessage from '@components/MyFavorites/ToastMessage/FavoritesToastMessage';
import redHeartIcon from '@assets/images/my-favorites-details-red-heart.svg';
import grayHeartIcon from '@assets/images/my-favorites-details-gray-heart.svg';
import updateHotelFavorites from '@helpers/updateHotelFavorites';
import BookingSection from '@components/BookingSection/BookingSection';
import { readReviewsClick } from '@events/DLP';
import { hotelFavoriteTappedOnDLP } from '@events/myFavorites';
import AmenitiesBottomDrawer from '@components/common/BottomDrawer/AmenitiesBottomDrawer';
import useFavorites from '@hooks/useFavorites';
import HotSpotBanner from '@components/HotSpot/HotSpotBanner';
import REVIEWS_THRESHOLD_TO_BE_SHOWN from '@constants/yotpo';
import StarRating from '@components/common/Yotpo/StarRating';
import { Separator } from '@solar/common/Separator';
import { useEvents } from '@events/EventsProvider';
import { VIEW_DLP_REVIEWS } from '@constants/amplitudeEvents';
import useAmplitudePayloads from '@hooks/useAmplitudePayloads';
import CaptureEmailDLPInLine from '@components/CaptureEmail/CaptureEmailDLPInLine';
import { useHotelContext } from '@context/HotelContext';
import Amenities from '@components/HotelCard/Amenities';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import { isTreatment } from '@helpers/experiment';
import TruncateText from '@components/common/TruncateText/TruncateText';
import MarkdownHotelDescription from './MarkdownHotelDescription';
import styles from './HotelDetails.module.css';

type Props = {
  scrollToReviews: () => void;
  setHideReviews: React.Dispatch<React.SetStateAction<boolean>>;
  setHideReviewsHeader: React.Dispatch<React.SetStateAction<boolean>>;
  averageScore: number;
  totalReviews: number;
  productsRef: RefObject<HTMLDivElement> | null;
  productSectionRef: RefObject<HTMLDivElement> | null;
};

export default function HotelDetails({
  scrollToReviews,
  setHideReviews,
  setHideReviewsHeader,
  averageScore,
  totalReviews,
  productsRef,
  productSectionRef,
}: Props) {
  const { date, hotel: hotelDetails, productDetails } = useHotelContext();
  const [showFullPSAWarning, setShowFullPSAWarning] = useState<boolean>(true);
  const [psaWarningFullHeight, setPsaWarningFullHeight] = useState<number>(0);
  const [showPSAWarningReadMoreText, setShowPSAWarningReadMoreText] = useState<boolean>(true);
  const [isAmenitiesDrawerOpen, setIsAmenitiesDrawerOpen] = useState<boolean>(false);
  const [saveToFavorites, setSaveToFavorites] = useState<boolean>(false);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [userFavoritesIds, setUserFavoritesIds] = useState<any>([]);
  const [showFavoriteToastMessage, setShowFavoriteToastMessage] = useState<boolean>(false);
  const [, setUserFavoritesMetadata] = useState<any>({});

  const psaWarningRef = useRef<any>(null);

  const [cookies, setCookie] = useCookies(['userInformation']);
  const { getItem, setItem } = useSessionStorage();
  const { addHotelToFavorites } = useFavorites();

  const togglePSAWarning = () => {
    setShowFullPSAWarning(!showFullPSAWarning);
  };

  const scrollToBookingSection = () => {
    const smartCalendarElement = document.getElementById('product-card-container');
    smartCalendarElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const { summarizedDescriptionExperiment } = useFeatureFlagContext();
  const isShowingChatGPTDescription = useMemo(
    () =>
      Boolean(hotelDetails.summarized_description) && isTreatment(summarizedDescriptionExperiment),
    [hotelDetails.summarized_description, summarizedDescriptionExperiment],
  );

  const handleAddingHotelIdToFavorites = () => {
    addHotelToFavorites(
      cookies,
      setCookie,
      hotelDetails,
      setShowFavoriteToastMessage,
      userFavoritesIds,
      setSaveToFavorites,
      setItem,
      getItem,
      setUserFavoritesIds,
    );

    if (!userFavoritesIds.includes(hotelDetails.id.toString())) {
      hotelFavoriteTappedOnDLP(hotelDetails.id, hotelDetails.name);
    }
  };

  useEffect(() => {
    if (userFavoritesIds.length === 0 && window.location.href.includes('hotels')) {
      updateHotelFavorites(
        cookies,
        null,
        setUserFavoritesMetadata,
        getItem,
        setUserFavoritesIds,
        null,
        null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (psaWarningRef && psaWarningRef.current) {
      const divHeight = psaWarningRef.current.offsetHeight;
      setPsaWarningFullHeight(divHeight);
      setShowPSAWarningReadMoreText(isShowReadMoreText(psaWarningRef, 4));
      setShowFullPSAWarning(false);
    }
  }, []);

  // remove read more button when clipped height of psa banner is same as it's full height
  useEffect(() => {
    if (!showFullPSAWarning && psaWarningFullHeight && psaWarningRef && psaWarningRef.current) {
      const divHeight = psaWarningRef.current.offsetHeight;
      if (divHeight === psaWarningFullHeight) {
        setShowPSAWarningReadMoreText(false);
      }
    }
  }, [showFullPSAWarning, psaWarningFullHeight]);

  const { track } = useEvents();
  const { generateHotelPayload } = useAmplitudePayloads();

  const onStarWidgetClick = () => {
    readReviewsClick(hotelDetails);
    scrollToReviews();
    const hotelPropertyGroup = generateHotelPayload(hotelDetails, undefined);
    track(VIEW_DLP_REVIEWS, {
      view_source: 'Star Widget',
      number_of_reviews: reviewsCount,
      not_enough_reviews: false,
      ...hotelPropertyGroup,
      hotel_reviews_count: reviewsCount,
    });
  };

  const productId =
    productDetails?.products[0]?.product_id ??
    productDetails?.products[0]?.children?.[0]?.product_id;

  const HotelDescriptionComponent = isShowingChatGPTDescription ? (
    <MarkdownHotelDescription>{hotelDetails.summarized_description}</MarkdownHotelDescription>
  ) : (
    <TruncateText lines={5}>{hotelDetails.description}</TruncateText>
  );

  return (
    <>
      {showFavoriteToastMessage ? (
        <FavoritesToastMessage inMobileView saveToFavorites={saveToFavorites} />
      ) : null}
      <div className="flex flex-col w-full pl-6 pr-6 text-base dlp-left-container font-rp-pn-regular -tracking-0.24 text-rp-primary-black d:w-2/3 d:pl-0 d:pr-0 d:text-17">
        <div
          data-testid="hotel-details-header"
          className="flex flex-col justify-between items-start d:flex-row d:mb-2"
        >
          {/* Hotel Name and Star Rating */}
          <div className="flex flex-col">
            <h2 className="text-25 leading-8 w-full font-rp-pn-semi-bold break-normal d:w-auto d:max-w-sm">
              {hotelDetails.name}
            </h2>
            <div className="mt-3 mb-2">
              <StarRating
                productId={productId}
                averageScore={averageScore}
                totalReviews={totalReviews}
                reviewsThreshold={REVIEWS_THRESHOLD_TO_BE_SHOWN}
                onClick={onStarWidgetClick}
                setHideReviews={setHideReviews}
                setReviewsCount={setReviewsCount}
                setHideReviewsHeader={setHideReviewsHeader}
              />
            </div>
            {hotelDetails.star_classification && (
              <p className="opacity-70 font-rp-pn-regular mt-1">
                {hotelDetails.star_classification} star property
              </p>
            )}
          </div>

          <div className="flex">
            {/* Mobile Vibes will go first */}
            {/* {hotelDetails.vibes && hotelDetails.vibes.primary && (
              <div className="flex items-center space-x-2.5">
                <span className="h-7 text-15 bg-rp-primary-new flex flex-shrink-0 font-medium items-center justify-center px-1.5 py-2 mr-2 rounded-md text-white -tracking-0.02em">
                  {hotelDetails.vibes.primary}
                </span>
              </div>
            )}

            {hotelDetails.vibes && hotelDetails.vibes.secondary && (
              <div className="flex items-center space-x-2.5">
                <span className="h-7 text-15 bg-rp-primary-new flex flex-shrink-0 font-medium items-center justify-center px-1.5 py-2 mr-2 rounded-md text-white -tracking-0.02em">
                  {hotelDetails.vibes.secondary}
                </span>
              </div>
            )} */}
          </div>
        </div>

        {/* Icons and read reviews button */}
        <div className="flex flex-col-reverse justify-between items-start d:items-center mt-3 h-full w-full relative d:mt-2 d:h-10 d:flex-row">
          <div className="flex w-full justify-start space-x-3 mb-3 d:mt-2 d:justify-start d:mb-0">
            {hotelDetails.amenities && (
              <Amenities amenities={hotelDetails.amenities} size="medium" />
            )}
          </div>

          <div className="flex items-center justify-end content-center h-full">
            <div className="flex justify-end cursor-pointer mr-2 relative d:h-full mb-3 d:mb-0 d:w-36">
              <Image
                src={
                  userFavoritesIds &&
                  userFavoritesIds.includes(hotelDetails.metadata.hotel_id.toString())
                    ? redHeartIcon
                    : grayHeartIcon
                }
                alt="heart"
                onClick={handleAddingHotelIdToFavorites}
              />
            </div>
          </div>
        </div>

        {date ? <HotSpotBanner hotel={hotelDetails} /> : null}

        <div ref={productsRef} />
        {/* Hotel Description */}
        <div
          className="flex flex-col gap-y-spacing-lg my-spacing-lg"
          data-testid="hotel-description"
        >
          <Separator className="h-px" />
          {HotelDescriptionComponent}
          <Separator className="h-px" />
        </div>
        {/* PSA Warning */}
        {hotelDetails.metadata && hotelDetails.metadata?.public_service_message?.trim() && (
          <div className="border border-solid border-rp-secondary-orange rounded-lg bg-yellow-50 mt-10 py-6 px-4 relative">
            <div className={`flex ${styles.listContainer} ${styles.psaBanner}`} id="psa-container">
              <div className="relative h-9 w-9 mr-5">
                <Image src={warningIcon} layout="fixed" alt="Warning Icon" />
              </div>
              <div className="flex flex-col items-start font-rp-pn-regular relative overflow-hidden">
                <div
                  ref={psaWarningRef}
                  className={`${
                    showFullPSAWarning
                      ? 'line-clamp-none overflow-hidden'
                      : 'line-clamp-4 overflow-hidden max-h-28 d:max-h-32'
                  }`}
                >
                  <ReactMarkdown>{hotelDetails.metadata.public_service_message}</ReactMarkdown>
                </div>
                {showPSAWarningReadMoreText && (
                  <button
                    className="underline font-rp-pn-semi-bold text-rp-primary-dark mt-1"
                    type="button"
                    onClick={togglePSAWarning}
                  >
                    {showFullPSAWarning ? 'read less' : 'read more'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <BookingSection
          productSectionRef={productSectionRef}
          productsRef={productsRef}
          scrollToBookingSection={scrollToBookingSection}
        />

        {/* Pool Hours */}
        {hotelDetails.metadata && (
          <div data-testid="pool-hours-section" className="flex flex-col pt-10 d:pt-8">
            <p className="font-rp-pn-semi-bold text-21 mb-7">Hours</p>
            <div className={`${styles.poolHours}`}>
              <ReactMarkdown>{hotelDetails.metadata.markdown_pool_hours_details}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* How it works */}
        {hotelDetails.how_it_works && (
          <div
            data-testid="how-it-works-section"
            className="bg-rp-light-gray px-6 pt-8 pb-5 rounded-xl flex flex-col mt-8 text-15 d:text-17 d:px-8 d:pb-8"
          >
            <p className="font-rp-pn-semi-bold mb-6 text-19 d:text-21 d:mb-8">How it works</p>
            {hotelDetails.how_it_works.map((item, index) => (
              <div key={item} className="flex justify-start items-start mb-3">
                <div className="flex mt-0.5">
                  <div className="w-6 h-6 bg-rp-green flex justify-center items-center rounded-full text-sm text-white text-center">
                    <p>{index + 1}</p>
                  </div>
                </div>
                <div className="pl-4 mt-px">{item}</div>
              </div>
            ))}
          </div>
        )}
        {/* Cancellation */}
        <div
          data-testid="cancellation-policy-section"
          className="bg-rp-light-gray px-6 pt-8 pb-5 rounded-xl flex flex-col mt-8 text-15 d:text-17 d:px-8 d:pb-8"
        >
          <div className="flex flex-col">
            <p className="font-rp-pn-semi-bold text-19 mb-2 d:text-21">Cancellation Policy</p>
            <div className="mb-6">
              Read our full{' '}
              <a
                target="_blank"
                title="Open in a new window"
                href={hotelDetails.changes_and_cancellation_policy.cancellation_policy_is}
                className="underline font-rp-pn-semi-bold"
                rel="noreferrer"
              >
                cancellation policy
              </a>
            </div>
          </div>
          <div className="flex flex-col">
            <p className="font-rp-pn-semi-bold text-19 mb-2 d:text-21">Cancel Online</p>
            <p className="mb-6">
              You can cancel your booking online for a full refund back to your original payment
              method or for ResortPass Credit to use another time. Bookings can be cancelled online
              up until the following times:
            </p>

            <div>
              {hotelDetails.cancel_for_rp_credit.map(
                (item: string, key: number) =>
                  item && (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={key} className="flex items-start">
                      <div className="relative mt-1 mr-6">
                        <Image src={infoIcon} layout="fixed" alt="Info icon" />
                      </div>
                      <div className="mb-3.5">{item}</div>
                    </div>
                  ),
              )}
            </div>
          </div>
        </div>

        <GoogleMap address={hotelDetails.address} />

        {/* Marketing Module - Digioh Email Capture */}
        <CaptureEmailDLPInLine />
      </div>
      {isAmenitiesDrawerOpen && hotelDetails.amenities && (
        <AmenitiesBottomDrawer
          amenities={hotelDetails.amenities}
          isAmenitiesDrawerOpen={isAmenitiesDrawerOpen}
          setIsAmenitiesDrawerOpen={setIsAmenitiesDrawerOpen}
        />
      )}
    </>
  );
}
